import { useParams, Link } from "react-router-dom";
import { allMovies } from "@/mocks/movies";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from "@/components/feature/SEOHead";

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const movie = allMovies.find((m) => m.id === id);
  const article = articles.find((a) => a.id === id);

  if (movie) {
    const cat = categories.find((c) => c.slug === movie.category);
    return (
      <>
        <WebPageSchema
          title={`${movie.title} - 木瓜影视`}
          description={movie.description}
          url={`https://www.muguays.com/article/${movie.id}`}
          dateModified={movie.releaseDate}
        />
        <BreadcrumbListSchema
          items={[
            { name: "首页", url: "https://www.muguays.com/" },
            { name: cat?.name || movie.category, url: `https://www.muguays.com/category/${movie.category}` },
            { name: movie.title, url: `https://www.muguays.com/article/${movie.id}` },
          ]}
        />
        <ArticleSchema
          title={movie.title}
          description={movie.description}
          url={`https://www.muguays.com/article/${movie.id}`}
          datePublished={movie.releaseDate}
          author={movie.director}
          imageUrl={movie.cover}
        />
        <header className="pt-20 pb-8 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${movie.category}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                {cat?.name || movie.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 line-clamp-1">{movie.title}</span>
            </nav>
          </div>
        </header>

        <section className="px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-72 flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 sticky top-24">
                  <img
                    src={movie.cover}
                    alt={movie.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {movie.rating > 0 && (
                    <span className="px-3 py-1 text-sm bg-accent-100 text-accent-700 rounded-md whitespace-nowrap font-medium">
                      {movie.rating} 分
                    </span>
                  )}
                  <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.year}</span>
                  <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.region}</span>
                  <span className="text-sm text-foreground-500 whitespace-nowrap">{movie.duration}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-6">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 rounded-xl bg-background-100">
                  <div>
                    <span className="text-xs text-foreground-500">导演</span>
                    <p className="text-sm text-foreground-900 mt-1">{movie.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">主演</span>
                    <p className="text-sm text-foreground-900 mt-1">{movie.actors.join(" / ")}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">上映日期</span>
                    <p className="text-sm text-foreground-900 mt-1">{movie.releaseDate}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-500">集数/状态</span>
                    <p className="text-sm text-foreground-900 mt-1">{movie.episodeInfo}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="font-heading text-base text-foreground-900 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {movie.detailContent}
                  </p>
                </div>

                <Link
                  to={`/category/${movie.category}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  在线观看 <i className="ri-play-circle-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (article) {
    const cat = categories.find((c) => c.slug === article.category);
    return (
      <>
        <WebPageSchema
          title={`${article.title} - 木瓜影视`}
          description={article.summary}
          url={`https://www.muguays.com/article/${article.id}`}
          dateModified={article.date}
        />
        <BreadcrumbListSchema
          items={[
            { name: "首页", url: "https://www.muguays.com/" },
            { name: cat?.name || article.category, url: `https://www.muguays.com/category/${article.category}` },
            { name: article.title, url: `https://www.muguays.com/article/${article.id}` },
          ]}
        />
        <ArticleSchema
          title={article.title}
          description={article.summary}
          url={`https://www.muguays.com/article/${article.id}`}
          datePublished={article.date}
          author={article.author}
          imageUrl={article.coverImage}
        />
        <header className="pt-20 pb-8 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                {cat?.name || article.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 mb-3">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
              <span>{article.author}</span>
              <span>{article.date}</span>
              <div className="flex flex-wrap gap-1.5">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </header>

        <section className="px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            {article.coverImage && (
              <div className="w-full aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <article
              className="prose-custom text-sm text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {article.relatedIds.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="font-heading text-base text-foreground-900 mb-4">相关推荐</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {article.relatedIds.map((rid) => {
                    const relatedMovie = allMovies.find((m) => m.id === rid);
                    if (!relatedMovie) return null;
                    return (
                      <Link
                        key={rid}
                        to={`/article/${rid}`}
                        className="group block cursor-pointer"
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-background-200">
                          <img
                            src={relatedMovie.cover}
                            alt={relatedMovie.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <h4 className="text-xs font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                          {relatedMovie.title}
                        </h4>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </>
    );
  }

  return (
    <div className="pt-20 pb-20 px-4 text-center">
      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
        <i className="ri-error-warning-line text-2xl"></i>
      </div>
      <h1 className="font-heading text-lg text-foreground-900 mb-2">内容未找到</h1>
      <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的内容不存在或已下架</p>
      <Link
        to="/"
        className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
      >
        返回首页
      </Link>
    </div>
  );
}